import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './Canvas.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { useApi } from '../../../api/api'
import { CanvasGrid } from '../../CanvasGrid/CanvasGrid'
import ToolBar from '../../ToolBar/ToolBar'
import SubNav from './components/SubNav/SubNav'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ROUTES } from '../../../router/routes'
import {
    ShareIcon,
    CloudArrowUpIcon,
    ProjectorScreenIcon,
    CursorIcon,
    BoundingBoxIcon,
    CaretRightIcon,
    CaretDownIcon,
} from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopMenu,
    ScoopMenuItem,
    ScoopText,
    ScoopRow,
    ScoopCenter,
    useToast,
    ScoopSpinner,
} from '../../ui'
import {
    setActiveMode,
    setZoom,
    setBackgroundColor,
    setLastSaved,
    setPresentationID,
    setShowFrameDrawer,
} from '../../../store/actions/uiActions'
import sha256 from 'crypto-js/sha256'
import { Server } from '../../../api/Server'
import { PromptDrawer } from '../../Objects/Prompt/PromptDrawer/PromptDrawer'
import { OBJECT_TYPES } from '../../Objects/types'

import { FrameDrawer } from '../../Objects/Frame/FrameDrawer'
import { setGuestPrompts } from '../../../store/actions/authActions'
import { useModal, MODALS } from '../../modals'
import {
    addArrow,
    addInsight,
    addKPI,
    addSheetlet,
    addTextEditor,
    addProcessDiagram,
    addImage,
    addVideo,
    addPrompt,
    addFrame,
    applyLoadedObjectsState,
} from '../../../store/actions'
import { useCanvases } from '../../../api/hooks/canvases/useCanvases'
import { useWorkspaceMetadata } from '../../../api/hooks/workspaceMetadata/useWorkspaceMetadata'
import { EVENTS, trackEvent } from '../../../events'
import { useWorkspaces } from '../../../api/hooks/workspaces/useWorkspaces'
import { handleUploadSlidesV2 } from './utils'
import { useIsTourWorkspace } from '../../../hooks/useIsTourWorkspace'

export const Canvas = () => {
    const isTourWorkspace = useIsTourWorkspace()
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const token = useSelector((state) => state.auth.token)
    const userFirstName = useSelector((state) => state.auth.userFirstName)
    const activeMode = useSelector((state) => state.ui.activeMode)
    const objects = useSelector((state) => state.objects)
    const zoom = useSelector((state) => state.ui.zoom)
    const presentationID = useSelector((state) => state.ui.presentationID)
    const lastSaved = useSelector((state) => state.ui.lastSaved)
    const selectedBackgroundColor = useSelector((state) => state.ui.backgroundColor || '#FFFFFF')
    const currentPrompt = useSelector((state) => state.prompt.currentPrompt)
    const passcode = useSelector((state) => state.auth.token)
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)
    const dispatch = useDispatch()
    const { state } = useLocation()
    const navigate = useNavigate()
    const apiPath = isGuestMode ? 'guest-canvasV2' : 'canvasV2'
    const { postData: postCanvasData } = useApi(
        `https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/${apiPath}`
    )
    const { postData: postThumbnailGenerator } = useApi(
        `https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/thumbnailgenerator`
    )
    const { canvasID } = useParams()
    const { canvases, renameCanvas, isLoading } = useCanvases()
    const { workspaces } = useWorkspaces()
    const { workspaceMetadata, refetch } = useWorkspaceMetadata()
    const canvasName = Array.isArray(canvases)
        ? canvases?.find((c) => c.canvasID === canvasID)?.canvasName
        : ''
    const [optionsMenuOpen, setOptionsMenuOpen] = useState(false)
    const [reordering, setReordering] = useState(false)
    const [itemToAdd, setItemToAdd] = useState(null)
    const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState(null)
    const [checksum, setChecksum] = useState(null) //checksum of the canvas objects
    const userIDRef = useRef(userID) // Ref to hold the current userID
    const checksumRef = useRef(checksum) // Ref to hold the current checksum
    const fullScreenContainer = useRef()
    const shareModal = useModal(MODALS.CANVAS.SHARE)
    const renameModal = useModal(MODALS.CANVAS.RENAME)
    const slidesModal = useModal(MODALS.CANVAS.SLIDES)
    const [lastActiveMode, setLastActiveMode] = useState('cursor')
    const [finishLoadObjects, setFinishLoadObjects] = useState(false)
    const [server, setServer] = useState(new Server(workspaceID, userID, token))

    // if this is a restricted invite, only allow a specific logged user to see this canvas
    const [isGuestAccountRestricted, setIsGuestAccountRestricted] = useState(false)
    const [restrictedGuestAccount, setRestrictedGuestAccount] = useState(null)
    const frames = objects.filter((obj) => obj.type === OBJECT_TYPES.FRAME)

    const toast = useToast()

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                setItemToAdd(null)
            }
        }
        window.addEventListener('keydown', handleEscape)
        return () => {
            window.removeEventListener('keydown', handleEscape)
            dispatch(setShowFrameDrawer(false))
            dispatch(applyLoadedObjectsState([]))
        }
    }, [])

    useEffect(() => {
        setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    const loadObjects = async () => {
        if (userID && workspaceID) {
            if (canvases && state !== 'newCanvas') {
                if (!canvases.some((c) => c.canvasID === canvasID) && !isLoading) {
                    navigate(ROUTES.CANVAS_DASHBOARD)
                }
            }
            const server = new Server(workspaceID, userID, token)
            const result = await server.postData(
                {
                    action: 'loadCanvasWithThemes',
                    userID: userID,
                    workspaceID: workspaceID,
                    canvasID: canvasID,
                },
                (result) => {
                    if (result) {
                        if (result?.canvas?.canvasObjects.length > 0) {
                            // MAP to fix new prompt min height
                            const objects = result?.canvas?.canvasObjects.map((obj) => {
                                if (obj.type === OBJECT_TYPES.PROMPT && obj.height < 100) {
                                    return { ...obj, height: 100 }
                                } else {
                                    return obj
                                }
                            })
                            if (result.themes?.length > 0) {
                                result.themes.forEach((theme, index) => {
                                    let fontsUrl = 'https://fonts.googleapis.com/css2?'
                                    theme.fonts.forEach((font, index) => {
                                        // this will need improvement for more complex fonts
                                        fontsUrl =
                                            fontsUrl +
                                            `family=${font.fontFamily.split(' ').join('+')}`
                                        if (font.variant) {
                                            if (font.variant.includes('italic'))
                                                fontsUrl = fontsUrl + ':ital'
                                            const weight = font.variant.replace(/\D/g, '')
                                            if (weight)
                                                fontsUrl =
                                                    fontsUrl +
                                                    (font.variant.includes('italic')
                                                        ? ',wght@'
                                                        : ':wght@') +
                                                    weight
                                        }
                                        if (index + 1 < theme.fonts.length)
                                            fontsUrl = fontsUrl + '&'
                                    })
                                    const link = document.createElement('link')
                                    link.rel = 'stylesheet'
                                    link.href = fontsUrl
                                    link.id = 'dynamic-loaded-fonts-' + index
                                    document.head.appendChild(link)
                                })
                            }
                            dispatch({
                                type: 'APPLY_LOADED_OBJECTS_STATE',
                                payload: objects,
                            })
                        } else {
                            dispatch({
                                type: 'APPLY_LOADED_OBJECTS_STATE',
                                payload: [],
                            })
                        }
                        let newZoom = parseFloat(result.canvas?.zoom)
                        dispatch(setZoom(newZoom || 1))
                        dispatch(setBackgroundColor(result.canvas?.background || '#FFFFFF'))
                        dispatch(setLastSaved(result.canvas?.lastSaved))
                        dispatch(setPresentationID(result.canvas?.presentationID))
                    }
                    if (result.canvas?.canvasID) {
                        dispatch({
                            type: 'UPDATE_CANVAS',
                            canvasID: result.canvas.canvasID,
                            canvasName: result.canvas.canvasName || 'Untitled Canvas',
                        })
                    }
                    setFinishLoadObjects(true)
                }
            )
        }
    }

    useEffect(() => {
        loadObjects()
    }, [userID, workspaceID, canvasID, workspaceID])

    useEffect(() => {
        if (userID && !userIDRef.current) {
            // console.log("setting userIDRef.current to ", userID)
            userIDRef.current = userID
        }
    }, [userID])

    useEffect(() => {
        const newChecksum = sha256(JSON.stringify(objects)).toString()
        setChecksum(newChecksum)
        checksumRef.current = newChecksum // Update the ref whenever checksum state changes
    }, [objects])

    useEffect(() => {
        const handleEsc = () => {
            if (!document.fullscreenElement) dispatch(setActiveMode(lastActiveMode))
        }
        window.addEventListener('fullscreenchange', handleEsc)
        return () => window.removeEventListener('fullscreenchange', handleEsc)
    }, [lastActiveMode])

    const redeemSharedInvite = useCallback(async (passcode) => {
        const action = {
            action: 'redeemInvite',
            userID: 'guest',
            passcode: passcode,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        try {
            const response = await fetch(
                'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/SharedInvite',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(action),
                }
            )
            const result = await response.json()
            // validate the last logged user is allowed to see this canvas
            if (result.requireAccount) {
                const storedEmail = localStorage.getItem('userEmail')
                if (result.invitedUser !== storedEmail) {
                    setIsGuestAccountRestricted(true)
                    setRestrictedGuestAccount(result.invitedUser)
                }
            }
            return result
        } catch (e) {
            console.error('Error: ', e)
        }
    })

    useEffect(() => {
        if (isGuestMode) {
            redeemSharedInvite(passcode).then((r) => {
                dispatch(setGuestPrompts(JSON.parse(r.promptData)))
            })
        }
    }, [isGuestMode])

    const handleRename = async () => {
        setOptionsMenuOpen(false)
        await renameModal.show({
            canvasID,
            currentName: canvasName,
        })
    }

    // probe the server every 20 secs if the user isn't active to see if there are any updates
    const probe = async () => {
        if (userIDRef.current && workspaceID) {
            const action = {
                action: 'probe',
                userID: userIDRef.current,
                workspaceID: workspaceID,
                canvasID: canvasID,
                checksum: checksumRef.current,
                isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
            }
            const result = await postCanvasData(action)
            if (result?.status == 'changed') {
                if (result?.canvasObjects?.length > 0) {
                    window.location.reload()
                }
            }
        }
    }

    const idleIntervalRef = useRef(null)

    const resetIdleInterval = () => {
        clearInterval(idleIntervalRef.current)
        idleIntervalRef.current = setInterval(() => {
            probe() // Call the probe function here
        }, 20000)
    }

    useEffect(() => {
        const handleUserActivity = () => {
            resetIdleInterval()
        }

        document.addEventListener('mousemove', handleUserActivity, {
            passive: true,
        })
        document.addEventListener('keydown', handleUserActivity, {
            passive: true,
        })

        resetIdleInterval()

        return () => {
            clearInterval(idleIntervalRef.current)
            document.removeEventListener('mousemove', handleUserActivity)
            document.removeEventListener('keydown', handleUserActivity)
        }
    }, [userID])

    const handlePresentation = () => {
        const frames = objects.filter((obj) => obj.type === OBJECT_TYPES.FRAME)
        if (frames.some((f) => !f.hidden)) {
            setLastActiveMode(activeMode)
            dispatch(setActiveMode('presentation'))
            navigate(`/canvas/${canvasID}/presentation/0` + window.location.search)
            const intervalId = setInterval(() => {
                const elem = document.getElementById('slide-container')
                if (elem && elem.requestFullscreen) {
                    elem.requestFullscreen().catch((err) => {
                        console.error('Fullscreen request failed:', err)
                    })
                    clearInterval(intervalId)
                }
            }, 100)
            setTimeout(() => {
                clearInterval(intervalId)
            }, 5000)
            if (workspaces.find((w) => w.workspaceID === workspaceID)?.name === 'Tour Scoop')
                trackEvent(EVENTS.CANVAS.VIEW_PRESENTATION)
        }
    }

    const getGlobalWrapperStyle = () => {
        const result = objects.find((obj) => obj?.wrapperStyles?.override === false)?.wrapperStyles
        if (result) {
            const newStyle = { ...result }
            newStyle.override = true
            return newStyle
        }
    }

    const getCenter = (e, deltaX, deltaY) => {
        const rect = document.getElementById('scoop-canvas').getBoundingClientRect()
        const x = (e.clientX - rect.left) / zoom + deltaX / zoom
        const y = (e.clientY - rect.top) / zoom + deltaY / zoom
        return { x, y }
    }

    const handleClickCanvas = (e) => {
        trackEvent(EVENTS.CANVAS.ADD_ASSET, {
            canvas: { canvasID: canvasID },
            asset: { type: itemToAdd },
        })
        const wrapperStyles = getGlobalWrapperStyle()
        setItemToAdd(null)
        let x, y
        let deltaX, deltaY

        switch (itemToAdd) {
            case 'KPI':
                ;({ x, y } = getCenter(e, 0, 0))
                dispatch(addKPI({ zoom, x, y }))
                break
            case 'Summary':
                deltaX = (700 * zoom) / 2
                deltaY = (500 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addInsight({ zoom, x, y, wrapperStyles }))
                break
            case 'Sheetlet':
                deltaX = (700 * zoom) / 2
                deltaY = (500 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addSheetlet({ zoom, x, y, wrapperStyles }))
                break
            case 'Image':
                deltaX = (800 * zoom) / 2
                deltaY = (500 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addImage({ zoom, x, y }))
                break
            case 'Video':
                deltaX = (800 * zoom) / 2
                deltaY = (500 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addVideo({ zoom, x, y }))
                break
            case 'Text Editor':
                deltaX = (150 * zoom) / 2
                deltaY = (50 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addTextEditor({ zoom, x, y }))
                break
            case 'Arrow':
                deltaX = (200 * zoom) / 2
                deltaY = 0
                ;({ x, y } = getCenter(e, -deltaX, deltaY))
                dispatch(addArrow({ zoom, x, y }))
                break
            case 'Process Diagram':
                deltaX = (800 * zoom) / 2
                deltaY = (500 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addProcessDiagram({ zoom, wrapperStyles, x, y }))
                break
            case 'Prompt':
                deltaX = (300 * zoom) / 2
                deltaY = (100 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(addPrompt({ zoom, x, y }))
                break
            case 'Frame':
                deltaX = (1280 * zoom) / 2
                deltaY = (720 * zoom) / 2
                ;({ x, y } = getCenter(e, -deltaX, -deltaY))
                dispatch(
                    addFrame({
                        zoom,
                        presentationIndex: objects.filter((obj) => obj.type === OBJECT_TYPES.FRAME)
                            .length,
                        x,
                        y,
                    })
                )
                break
        }
    }

    const logout = () => {
        navigate(ROUTES.LOGOUT)
    }

    const shouldRenderCanvas = activeMode === 'edit' || activeMode === 'cursor'

    return (
        <div className={`${styles.canvasRoot} ${isGuestMode ? '' : styles.canvasRootWithPadding}`}>
            <SubNav>
                <ScoopRow className={styles.subNavActions}>
                    <ScoopButton
                        variant={activeMode === 'cursor' ? 'default' : 'text'}
                        size="sm"
                        onClick={() => {
                            dispatch(setActiveMode('cursor'))
                        }}
                    >
                        <CursorIcon size={18} weight="bold" />
                    </ScoopButton>
                    {!isGuestMode && !isTourWorkspace && (
                        <ScoopButton
                            name={'toggle-edit-button'}
                            variant={activeMode === 'edit' ? 'default' : 'text'}
                            size="sm"
                            onClick={() => {
                                dispatch(setActiveMode('edit'))
                            }}
                        >
                            <BoundingBoxIcon size={18} weight="bold" />
                        </ScoopButton>
                    )}
                    <ScoopButton
                        variant={activeMode === 'presentation' ? 'default' : 'text'}
                        size="sm"
                        onClick={handlePresentation}
                    >
                        <ProjectorScreenIcon size={18} weight="bold" />
                    </ScoopButton>
                </ScoopRow>
                <ScoopRow className={styles.subNavActions}>
                    {!isGuestMode && (
                        <>
                            <ScoopText
                                className={styles.subNavCanvasName}
                                onClick={() => navigate(ROUTES.CANVAS_DASHBOARD)}
                            >
                                Canvases
                            </ScoopText>
                            <CaretRightIcon size={16} weight="bold" />
                        </>
                    )}
                    <span
                        className={styles.subNavCanvasNameActive}
                        onClick={isTourWorkspace ? undefined : () => setOptionsMenuOpen(true)}
                        ref={setOptionsMenuAnchorEl}
                    >
                        {canvasName || 'Loading...'}
                    </span>
                    {!isGuestMode && !isTourWorkspace && <CaretDownIcon size={16} weight="bold" />}
                </ScoopRow>
                {!isGuestMode && !isTourWorkspace && (
                    <ScoopRow className={styles.subNavActions} gap="lg">
                        {frames.length > 0 && (
                            <ScoopButton
                                variant="text"
                                size="sm"
                                leftSection={<CloudArrowUpIcon size={20} weight="bold" />}
                                onClick={() => {
                                    const frames = objects
                                        .filter((obj) => obj.type === OBJECT_TYPES.FRAME)
                                        .sort((a, b) => a.presentationIndex - b.presentationIndex)
                                    handleUploadSlidesV2(
                                        presentationID,
                                        [],
                                        toast,
                                        slidesModal,
                                        frames,
                                        objects,
                                        server,
                                        userID,
                                        workspaceID,
                                        canvasID,
                                        postThumbnailGenerator
                                    )
                                }}
                            >
                                Sync to Slides
                            </ScoopButton>
                        )}
                        <ScoopButton
                            variant="text"
                            size="sm"
                            leftSection={<ShareIcon size={20} weight="bold" />}
                            onClick={() => shareModal.show()}
                        >
                            Share
                        </ScoopButton>
                    </ScoopRow>
                )}
            </SubNav>
            {isGuestAccountRestricted ? (
                <ScoopCenter className={styles.guestRestricted}>
                    <div className={styles.warningAlert}>
                        You must be logged in as {restrictedGuestAccount} to view this Canvas.
                        Please
                        <ScoopButton variant="text" onClick={logout}>
                            log in
                        </ScoopButton>
                        with (or create) the correct account.
                    </div>
                </ScoopCenter>
            ) : (
                <div
                    className={styles.canvasContainer}
                    ref={fullScreenContainer}
                    id={'root-canvas-container'}
                >
                    {shouldRenderCanvas && (
                        <>
                            <div
                                onClick={itemToAdd ? handleClickCanvas : () => setItemToAdd(null)}
                                className={styles.canvasScrollable}
                                style={{ '--canvas-bg-color': selectedBackgroundColor }}
                                id={'canvas-scrollable'}
                            >
                                {reordering ? (
                                    <ScoopCenter className={styles.loadingContainer}>
                                        <ScoopSpinner size={100} />
                                    </ScoopCenter>
                                ) : (
                                    <CanvasGrid
                                        width={50000}
                                        height={50000}
                                        workspaceID={workspaceID}
                                        workspaceMetadata={workspaceMetadata}
                                        userID={userID}
                                        canvasID={canvasID}
                                        userFirstName={userFirstName}
                                        canvasName={canvasName}
                                        itemToAdd={itemToAdd}
                                        slides={objects?.filter(
                                            (obj) => obj.type === OBJECT_TYPES.FRAME
                                        )}
                                        readyToRedirect={finishLoadObjects}
                                        isTourWorkspace={isTourWorkspace}
                                    />
                                )}
                                <PromptDrawer
                                    workspaceMetadata={workspaceMetadata}
                                    currentPromptObject={
                                        objects.filter((obj) => {
                                            const promptId = currentPrompt
                                                ? parseFloat(currentPrompt.split('-')[1])
                                                : ''
                                            return obj.id === promptId
                                        })[0]
                                    }
                                    workspaceID={workspaceID}
                                />
                            </div>
                            <FrameDrawer
                                reordering={reordering}
                                setReordering={setReordering}
                                isTourWorkspace={isTourWorkspace}
                            />
                            {activeMode === 'edit' && !isTourWorkspace && (
                                <ToolBar
                                    itemToAdd={itemToAdd}
                                    setItemToAdd={setItemToAdd}
                                    inboxes={workspaceMetadata?.inboxes || []}
                                />
                            )}
                        </>
                    )}
                </div>
            )}
            {!isGuestMode && (
                <ScoopMenu
                    onClose={() => setOptionsMenuOpen(false)}
                    open={optionsMenuOpen}
                    anchorEl={optionsMenuAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                    <ScoopMenuItem primary="Rename" onClick={handleRename} />
                </ScoopMenu>
            )}
        </div>
    )
}
